import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { PricingCards } from "../components/PricingCards";
import {
  fetchUsage,
  foundingLockInStatus,
  resendVerificationEmail,
} from "../lib/api";
import { Link, useLocation } from "react-router-dom";
import { track } from "../lib/analytics";
import { trackViewContent } from "../lib/marketingPixels";
import { useReaderAppMode } from "../lib/readerAppMode";
import { toast } from "sonner";

const Pricing = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const location = useLocation();
  const pricingParams = new URLSearchParams(location.search);
  const canceled = pricingParams.get("canceled") === "1";
  const requiresPlanChoice = pricingParams.get("trial_signup") === "1";
  const readerApp = useReaderAppMode();

  const resendVerification = async () => {
    if (resendingVerification) return;
    setResendingVerification(true);
    try {
      const result = await resendVerificationEmail();
      toast.success(
        result?.cooldown
          ? t("pricing.verifyEmailRecentlySent")
          : t("pricing.verifyEmailResent"),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pricing.verifyEmailResendError"));
    } finally {
      setResendingVerification(false);
    }
  };

  useEffect(() => {
    track("paywall_shown", { source: "pricing_page" });
    // TikTok/Taboola/GTM: "user viewed the offer page" — feeds the
    // ViewContent funnel event for ad optimization audiences.
    trackViewContent({ content_type: "pricing_page", page: "/pricing" });
    if (!user) return;
    fetchUsage().then(setUsage).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (readerApp) return;
    foundingLockInStatus().then(setFoundingStatus).catch(() => {});
  }, [readerApp]);

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <section className="pt-32 pb-24 px-6 sm:px-10" data-testid="pricing-hero">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-4">
              {t("pricing.kicker")}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#2C2123] leading-tight mb-4">
              {t("pricing.headline")}{" "}
              <span className="font-serif italic text-[#5D4050]">
                {t("pricing.headlineGlow")}
              </span>
              {readerApp ? t("pricing.headlineSuffixReader") : t("pricing.headlineSuffix")}
            </h1>
            <p className="text-lg text-[#5C4B51] max-w-2xl mx-auto">
              {readerApp ? t("pricing.sublineReader") : t("pricing.subline")}
            </p>
            {!readerApp && requiresPlanChoice && (
              <div
                className="mt-6 mx-auto max-w-xl rounded-2xl border border-[#D5A8A4] bg-white/80 px-5 py-4 text-[#5D4050]"
                data-testid="trial-plan-choice-required"
              >
                <strong className="font-semibold">{t("pricing.trialChoiceTitle")}</strong>{" "}
                {t("pricing.trialChoiceBody")}
              </div>
            )}
            {!readerApp && user && user.email_verified === false && (
              <div
                className="mt-4 mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900"
                data-testid="email-verification-required"
              >
                <strong className="font-semibold">{t("pricing.verifyEmailTitle")}</strong>{" "}
                {t("pricing.verifyEmailBody")}
                <button
                  type="button"
                  onClick={resendVerification}
                  disabled={resendingVerification}
                  className="mt-3 block mx-auto rounded-full border border-amber-400 px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-50"
                  data-testid="resend-verification-email"
                >
                  {resendingVerification
                    ? t("pricing.verifyEmailResending")
                    : t("pricing.verifyEmailResend")}
                </button>
              </div>
            )}
            {!readerApp && canceled && (
              <div
                className="mt-6 inline-block bg-[#FFF6F2] border border-[#EADDD7] rounded-full px-5 py-2 text-sm text-[#5D4050]"
                data-testid="checkout-canceled-banner"
              >
                {t("pricing.canceled")}
              </div>
            )}
            {!readerApp && usage && usage.tier === "free" && (
              <div
                className="mt-6 inline-block bg-white/70 border border-[#EADDD7] rounded-full px-5 py-2 text-sm text-[#5D4050]"
                data-testid="usage-pill"
              >
                {t("pricing.usagePill", {
                  remaining: usage.routines_remaining,
                  limit: usage.monthly_limit,
                })}
              </div>
            )}
            {!readerApp && usage && usage.tier === "pro" && (
              <div
                className="mt-6 inline-block bg-[#9B6A6C] text-white rounded-full px-5 py-2 text-sm"
                data-testid="usage-pill-pro"
              >
                {usage.pro_lifetime
                  ? t("pricing.proLifetimeActive")
                  : t("pricing.proActiveUntil", {
                      date: usage.pro_until
                        ? new Date(usage.pro_until).toLocaleDateString()
                        : "",
                    })}
              </div>
            )}
          </div>

          {!readerApp && !requiresPlanChoice && foundingStatus && !foundingStatus.is_full && (
            <Link
              to="/lock-in"
              className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-[#D5A8A4] bg-white/80 px-6 py-5 shadow-[0_18px_45px_-30px_rgba(93,64,80,0.55)] hover:bg-white transition-colors"
              data-testid="founding-lifetime-offer"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-1">
                  {t("pricing.foundingKicker", {
                    remaining: foundingStatus.remaining,
                  })}
                </p>
                <p className="text-xl font-serif text-[#2C2123]">
                  {t("pricing.foundingHeadline")}
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-[#9B6A6C] px-5 py-2.5 text-sm font-medium text-white">
                {t("pricing.foundingCta")}
              </span>
            </Link>
          )}

          <PricingCards
            currentTier={usage?.tier || "free"}
            requirePlanChoice={requiresPlanChoice}
          />

          {!readerApp && !requiresPlanChoice && (
            <Link
              to="/enterprise"
              className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-[#EADDD7] bg-white/70 px-6 py-5 hover:bg-white transition-colors"
              data-testid="pricing-enterprise-link"
            >
              <div>
                <p className="font-serif text-xl text-[#2C2123] mb-1">
                  {t("enterprise.pricingTitle")}
                </p>
                <p className="text-sm text-[#5C4B51]">
                  {t("enterprise.pricingBody")}
                </p>
              </div>
              <span className="shrink-0 text-sm font-medium text-[#805557]">
                {t("enterprise.pricingCta")} →
              </span>
            </Link>
          )}

          {!readerApp && (
            <p
              className="mt-10 text-center text-sm text-[#8A7D82] max-w-xl mx-auto"
              data-testid="pricing-footer-note"
            >
              {/* `<Trans>` whitelists the structural tags the
                  translation file is allowed to render. Removes the
                  need for `dangerouslySetInnerHTML` even though the
                  string is internally controlled — defense in depth.
                */}
              <Trans
                i18nKey="pricing.footerNote"
                components={{ b: <strong /> }}
              />
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
